"use client";

/**
 * P2-06a · Hero background video (client).
 *
 * The poster renders first and always — it is the server-painted LCP frame, so
 * there is no blank box and no CLS. The `<video>` only mounts once we've
 * confirmed motion is allowed, so `prefers-reduced-motion` users keep the
 * static poster and never see autoplay. Muted + `playsInline` so mobile
 * browsers permit autoplay; looped and non-interactive (decorative).
 */
import { useEffect, useState } from "react";

import { StoreImage } from "~/components/ui/store-image";
import type { HeroMedia } from "~/server/content/blocks/hero";

export function HeroVideo({
  media,
  priority,
}: {
  media: Extract<HeroMedia, { kind: "video" }>;
  priority: boolean;
}) {
  const [motionOk, setMotionOk] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setMotionOk(!mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  return (
    <>
      <StoreImage
        src={media.posterUrl}
        alt=""
        fill
        preset="hero"
        blurDataUrl={media.blurDataUrl}
        priority={priority}
        className="object-cover"
      />
      {motionOk && (
        <video
          aria-hidden
          autoPlay
          muted
          loop
          playsInline
          poster={media.posterUrl}
          className="absolute inset-0 h-full w-full object-cover"
        >
          {media.mobileUrl && (
            <source src={media.mobileUrl} media="(max-width: 640px)" />
          )}
          <source src={media.url} />
        </video>
      )}
    </>
  );
}
